/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */

/*
 * La pagina che si apre cliccando il link ricevuto per email.
 *
 * Il token viaggia nella query string ed e' l'unica prova d'identita' che c'e':
 * non si mostra a schermo, non si mette in localStorage e non si scrive nei log.
 * Vive il tempo di una richiesta e poi il server lo brucia.
 */
import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, ArrowLeft } from 'lucide-react';
import { useImpostazioni, urlImmagine } from '../hooks/useImpostazioni';
import { authAPI } from '../api/auth';

const ACCENT = '#10B981';
const MIN_CARATTERI = 8;

export default function ReimpostaPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [conferma, setConferma] = useState('');
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState('');
  const [fatto, setFatto] = useState(false);

  const impostazioni = useImpostazioni();
  const logo = urlImmagine(impostazioni.app_logo_url);

  // Controlli detti mentre si scrive, non dopo aver premuto il bottone.
  const troppoCorta = password.length > 0 && password.length < MIN_CARATTERI;
  const diverse = conferma.length > 0 && password !== conferma;
  const puoSalvare = password.length >= MIN_CARATTERI && password === conferma;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrore('');
    if (!puoSalvare) return;
    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setFatto(true);
      // Tre secondi per leggere la conferma, poi al login.
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setErrore(err.response?.data?.error || 'Non riesco a salvare la password. Riprova.');
      setLoading(false);
    }
  };

  // Link aperto senza token: succede copiando male l'indirizzo dall'email.
  if (!token) {
    return (
      <Guscio logo={logo} nomeApp={impostazioni.app_nome} titolo="Link non valido">
        <div style={styles.errBox}>
          <AlertTriangle size={22} color="#B91C1C" />
          <div>
            Questo indirizzo non contiene nessun codice di conferma. Apri il link
            direttamente dall'email, senza copiarlo a pezzi.
          </div>
        </div>
        <div style={styles.tornaRow}>
          <Link to="/password-dimenticata" style={{ ...styles.tornaLink, color: ACCENT }}>
            Chiedi un link nuovo
          </Link>
        </div>
      </Guscio>
    );
  }

  if (fatto) {
    return (
      <Guscio logo={logo} nomeApp={impostazioni.app_nome} titolo="Password aggiornata">
        <div style={styles.okBox}>
          <CheckCircle2 size={22} color="#047857" />
          <div>
            Fatto. Da adesso entri con la password nuova; fra un istante ti portiamo
            alla pagina di accesso.
          </div>
        </div>
        <p style={styles.nota}>
          Se eri gia' entrato da un altro telefono o computer, quegli accessi sono
          stati chiusi: dovrai rientrare anche li'.
        </p>
        <div style={styles.tornaRow}>
          <Link to="/login" style={{ ...styles.tornaLink, color: ACCENT }}>Vai all'accesso</Link>
        </div>
      </Guscio>
    );
  }

  return (
    <Guscio logo={logo} nomeApp={impostazioni.app_nome} titolo="Scegli una password nuova">
      <form onSubmit={onSubmit} style={styles.form}>
        <label style={styles.label}>
          Password nuova
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
            autoComplete="new-password"
            style={{ ...styles.input, ...(troppoCorta ? styles.inputErrato : {}) }}
          />
        </label>
        {troppoCorta
          ? <div style={styles.erroreCampo}><AlertTriangle size={13} /> Almeno {MIN_CARATTERI} caratteri</div>
          : <div style={styles.hint}>Almeno {MIN_CARATTERI} caratteri.</div>}

        <label style={styles.label}>
          Ripeti la password
          <input
            type="password"
            value={conferma}
            onChange={(e) => setConferma(e.target.value)}
            required
            autoComplete="new-password"
            style={{ ...styles.input, ...(diverse ? styles.inputErrato : {}) }}
          />
        </label>
        {diverse && <div style={styles.erroreCampo}><AlertTriangle size={13} /> Le due password non coincidono</div>}

        {errore && <div style={styles.error}>{errore}</div>}

        <button
          type="submit"
          disabled={loading || !puoSalvare}
          style={{ ...styles.button, backgroundColor: ACCENT, opacity: loading || !puoSalvare ? 0.5 : 1, cursor: loading || !puoSalvare ? 'not-allowed' : 'pointer' }}
        >
          {loading ? 'Salvataggio...' : 'Salva la password'}
        </button>
      </form>

      <div style={styles.tornaRow}>
        <Link to="/login" style={{ ...styles.tornaLink, color: ACCENT }}>
          <ArrowLeft size={15} /> Torna all'accesso
        </Link>
      </div>
    </Guscio>
  );
}

// La cornice e' sempre la stessa nei quattro stati della pagina.
function Guscio({ logo, nomeApp, titolo, children }) {
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {logo && <img src={logo} alt="" style={styles.logo} />}
        <h1 style={{ ...styles.title, color: ACCENT }}>{titolo}</h1>
        <p style={styles.subtitle}>{nomeApp}</p>
        {children}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F3F4F6', padding: '1rem' },
  card: { backgroundColor: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.07)', width: '100%', maxWidth: '400px', boxSizing: 'border-box' },
  logo: { width: '64px', height: '64px', objectFit: 'contain', display: 'block', margin: '0 auto 0.75rem' },
  title: { fontSize: '1.4rem', fontWeight: 700, textAlign: 'center', margin: '0 0 0.25rem' },
  subtitle: { textAlign: 'center', color: '#6B7280', fontSize: '0.9rem', margin: '0 0 1.25rem' },
  form: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  label: { display: 'flex', flexDirection: 'column', gap: '0.35rem', fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginTop: '0.4rem' },
  input: { padding: '0.65rem 0.75rem', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', width: '100%' },
  inputErrato: { borderColor: '#DC2626', backgroundColor: '#FEF2F2' },
  button: { padding: '0.7rem', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.95rem', fontWeight: 600, marginTop: '0.75rem', minHeight: '44px' },
  hint: { fontSize: '0.78rem', color: '#6B7280' },
  erroreCampo: { display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.78rem', color: '#DC2626', fontWeight: 600 },
  error: { padding: '0.6rem 0.75rem', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '0.85rem', marginTop: '0.5rem' },
  errBox: { display: 'flex', gap: '0.65rem', alignItems: 'flex-start', padding: '0.85rem', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', borderRadius: '10px', fontSize: '0.88rem', lineHeight: 1.5 },
  okBox: { display: 'flex', gap: '0.65rem', alignItems: 'flex-start', padding: '0.85rem', backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0', color: '#065F46', borderRadius: '10px', fontSize: '0.88rem', lineHeight: 1.5 },
  nota: { fontSize: '0.8rem', color: '#6B7280', marginTop: '0.85rem', lineHeight: 1.5 },
  tornaRow: { marginTop: '1.25rem', textAlign: 'center' },
  tornaLink: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.88rem', fontWeight: 600, textDecoration: 'none' },
};
